import { IonText } from "@ionic/react";
import { DetailedHTMLProps, HTMLAttributes, KeyboardEvent, ReactNode } from "react";
import { FieldError } from "react-hook-form";

import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import RichTextareaDragAndDrop from "@components/fields/RichTextareaDragAndDrop";
import styles from "@components/fields/TextareaWrapper.module.css";
import Icon from "@components/text/Icon";

type InputType = HTMLDivElement | HTMLTextAreaElement;

type Props = React.PropsWithChildren<{
  dragToUpload?: boolean;
  error: FieldError | undefined;
  helperText?: string;
  inputId: string;
  label?: string;
  required?: boolean;
  setFocus?: () => void;
}>;

const TextareaWrapper = ({
  children,
  dragToUpload = false,
  error,
  helperText,
  inputId,
  label,
  required = false,
  setFocus
}: Props) => {
  // In general, we want forms to submit on enter, but not on a textarea. A regular native form
  // should handle this behavior naturally, but a) the rich textarea is just a div, and b) the hook
  // form library doesn't seem to support the native enter submission, so we have our own submission
  // handler for the enter keypress, and we don't want enter events from these textareas to bubble
  // up to that handler.
  const handleKeyDown = (event: KeyboardEvent<InputType>) => {
    if (event.key === "Enter") {
      event.stopPropagation();
    }
  };

  const hasError = !!error && !!error.message;

  const wrapperClasses = [styles.textareaContainer];
  if (hasError) wrapperClasses.push(styles.hasError);

  const textareaInterior: ReactNode = (
    <>
      {label && (
        <div className={styles.labelContainer}>
          <label htmlFor={inputId}>
            {label}
            {required && <RequiredFieldIndicator />}
            {hasError && (
              <div className="ion-float-end">
                <Icon color="danger" icon="circleExclamation" />
              </div>
            )}
          </label>
        </div>
      )}
      {children}
    </>
  );

  const sharedDivProps: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> = {
    className: wrapperClasses.join(" "),
    onClick: () => (setFocus ? setFocus() : null),
    onKeyDown: handleKeyDown
  };

  return (
    <>
      {dragToUpload ? (
        <RichTextareaDragAndDrop {...sharedDivProps}>{textareaInterior}</RichTextareaDragAndDrop>
      ) : (
        <div {...sharedDivProps}>{textareaInterior}</div>
      )}
      {hasError && (
        <div className={styles.supportingText} id={`${inputId}-error`}>
          <IonText color="danger">{error.message}</IonText>
        </div>
      )}
      {!hasError && helperText && (
        <div className={styles.supportingText} id={`${inputId}-helper-text`}>
          <IonText color="gray-500">{helperText}</IonText>
        </div>
      )}
    </>
  );
};

export default TextareaWrapper;
